import {
  Button,
  InputAdornment,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import {
  DataGrid,
  DataGridProps,
  GridColumns,
  GridRenderCellParams,
  GridRowParams,
  GridOverlay
} from '@mui/x-data-grid'
import React from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { MUIExternalLink } from 'components/common/MUIExternalLink'
import PageSection from 'components/common/PageSection'
import { StyledRouterLink } from 'components/common/StyledRouterLink'
import { TruncatedTextWithTooltip } from 'components/common/TruncatedTextWithTooltip'
import Icon from 'components/Icon'
import {
  getLogo,
  getPresetKeyFromURL
} from 'components/scenarios/create/status-checks/third-party-presets'
import { useGetStatusChecksUrls } from 'components/status-checks/hooks/use-get-status-checks-urls'
import DeleteStatusCheck from 'components/status-checks/list/DeleteStatusCheck'
import {
  getMonitorPageUrl,
  isCustomStatusCheck
} from 'components/status-checks/status-checks-form-utils'
import { useStatusChecksSearchContext } from 'components/status-checks/StatusChecksSearchState'
import { useStatusChecksContext } from 'context/status-checks/StatusChecksContext'
import useSearch from 'hooks/use-search'
import { useTranslation } from 'hooks/use-translation'
import toast from 'lib/toast'
import theme from 'theme'
import { StatusCheck } from 'types/api/status-checks/status-check'
import { naturalSortBy } from 'utils/list'
import {
  defaultGridProps,
  getGridStyles,
  safeRowClick
} from 'utils/mui-components/data-grid-utils'
import { messageFromResponseError } from 'utils/strings'
import { getIntegrationTypeFromObservabilityToolTypeAndDomain } from 'utils/team-external-integrations/team-external-integrations-util'

const EmptyState: React.FC = () => {
  const { trans } = useTranslation()

  return (
    <GridOverlay>
      <Typography>{trans('status_checks.list.empty_text')}</Typography>
    </GridOverlay>
  )
}

// TODO add tests
export const StatusCheckList: React.FC = () => {
  const statusChecksUrls = useGetStatusChecksUrls()
  const navigate = useNavigate()
  const { trans } = useTranslation()
  const {
    statusChecksRaw: statusChecks,
    isFetching: isFetchingStatusChecks,
    isError: hasErrorStatusChecks,
    error: statusChecksError,
    refetch: reFetchStatusChecks
  } = useStatusChecksContext()

  const sortedStatusChecks = isFetchingStatusChecks
    ? []
    : naturalSortBy(statusChecks, 'name', 'asc')

  const searchState = useStatusChecksSearchContext()
  const { searchValue, setSearchValue, pageNum, setPageNum } =
    searchState.statusChecks

  const searchMapFn = React.useCallback((statusCheck: StatusCheck) => {
    // add status check type to searchable items list
    const statusCheckType = isCustomStatusCheck(statusCheck)
      ? 'custom'
      : getPresetKeyFromURL(statusCheck.endpointConfiguration?.url)
    return [statusCheck.name, statusCheckType].filter(Boolean)
  }, [])
  const searchStatusChecks = useSearch(sortedStatusChecks, searchMapFn)

  const filteredStatusChecks = React.useMemo(
    () => searchStatusChecks(searchValue),
    [searchValue, searchStatusChecks]
  )

  const handleSearchInputChange = ({ target: { value } }) => {
    setSearchValue(String(value))
  }

  const handleRowClick: DataGridProps['onRowClick'] = (
    params: GridRowParams<StatusCheck>,
    event
  ) => {
    const statusCheck = params.row
    safeRowClick(event, () => {
      navigate(statusChecksUrls.edit(statusCheck.identifier))
    })
  }

  const renderName = (params: GridRenderCellParams<StatusCheck>) => {
    const integrationType =
      getIntegrationTypeFromObservabilityToolTypeAndDomain(
        params.row.teamExternalIntegration.observabilityToolType,
        params.row.teamExternalIntegration.domain
      )

    return (
      <Stack
        data-testid="status-check-list-row"
        direction="row"
        spacing={2}
        sx={{ alignItems: 'center' }}
      >
        <Link to={statusChecksUrls.edit(params.row.identifier)}>
          {isCustomStatusCheck(params.row) ? (
            <Icon
              css={{
                color: 'initial',
                width: theme.space[5],
                height: theme.space[5]
              }}
              icon="wavePulse"
            />
          ) : (
            <img
              css={{
                color: theme.colors.gray[1],
                width: theme.fontSizes[5],
                height: theme.fontSizes[5]
              }}
              src={getLogo(
                getPresetKeyFromURL(params.row.endpointConfiguration?.url)
              )}
            />
          )}
        </Link>
        <Stack direction="column">
          <Link to={statusChecksUrls.edit(params.row.identifier)}>
            <TruncatedTextWithTooltip
              data-test-status-check-list-row-name={params.row.name}
              tooltipProps={{
                title: params.row.name
              }}
            >
              {params.row.name}
            </TruncatedTextWithTooltip>
          </Link>
          {params.row.endpointConfiguration?.url && (
            <Stack alignItems="baseline" direction="row" spacing={1}>
              <TruncatedTextWithTooltip
                color={theme.colors.steel[1]}
                sx={{
                  // ensure that the below link icon can appear despite length of text
                  // a better solution is based on the table width
                  maxWidth: 320
                }}
                tooltipProps={{
                  placement: 'bottom',
                  title: params.row.endpointConfiguration.url
                }}
                variant="caption"
              >
                {params.row.endpointConfiguration.url}
              </TruncatedTextWithTooltip>
              {params.row.rawEndpointConfiguration?.url && (
                <MUIExternalLink
                  href={getMonitorPageUrl(
                    params.row.rawEndpointConfiguration.url,
                    integrationType
                  )}
                >
                  <Icon icon="arrowUpRightFromSquare" />
                </MUIExternalLink>
              )}
            </Stack>
          )}
        </Stack>
      </Stack>
    )
  }

  const renderActions = (params: GridRenderCellParams<StatusCheck>) => {
    return (
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <Button
          component={StyledRouterLink}
          data-test-edit-status-check={params.row.identifier}
          data-track-action="edit-status-check"
          data-track-category="status-checks" // TODO track anything different if it's PNI?
          data-track-on="click"
          size="small"
          startIcon={<Icon icon="edit" />}
          to={statusChecksUrls.edit(params.row.identifier)}
          variant="contained"
        >
          {trans('forms.buttons.edit.label')}
        </Button>
        <Button
          component={StyledRouterLink}
          data-test-clone-status-check={params.row.identifier}
          data-track-action="clone-status-check"
          data-track-category="status-checks" // TODO track anything different if it's PNI?
          data-track-on="click"
          size="small"
          startIcon={<Icon icon="clone" />}
          state={{
            statusCheck: { ...params.row, identifier: null }
          }}
          to={statusChecksUrls.new()}
          variant="contained"
        >
          {trans('forms.buttons.clone.label')}
        </Button>
        <DeleteStatusCheck
          statusCheck={params.row}
          onChanged={async () => {
            toast({
              type: 'success',
              text: trans('status_checks.list.delete.success', {
                name: params.row.name
              })
            })
            await reFetchStatusChecks()
          }}
        />
      </Stack>
    )
  }

  const tableColumns: GridColumns<StatusCheck> = [
    {
      field: 'name',
      headerName: trans('status_checks.list.headers.name'),
      flex: 3, // TODO change back to 2 when clone button is re-enabled
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      disableReorder: true,
      renderCell: renderName
    },
    {
      field: 'none',
      headerName: trans('status_checks.list.headers.actions'),
      flex: 1,
      minWidth: 300,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      disableReorder: true,
      renderCell: renderActions
    }
  ]

  return (
    <PageSection
      errorMessage={messageFromResponseError(
        statusChecksError,
        trans('status_checks.list.retrieve.error')
      )}
      hasError={hasErrorStatusChecks}
      isLoading={isFetchingStatusChecks}
    >
      <Stack direction="row">
        <TextField
          data-test-status-check-list-search
          disabled={isFetchingStatusChecks}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon icon="search" />
              </InputAdornment>
            )
          }}
          label={trans('status_checks.list.search')}
          size="small"
          value={searchValue}
          onChange={handleSearchInputChange}
        />
      </Stack>
      <DataGrid<StatusCheck>
        {...defaultGridProps}
        columns={tableColumns}
        components={{
          NoRowsOverlay: EmptyState
        }}
        getRowId={(row) => row.identifier}
        page={pageNum}
        rows={filteredStatusChecks}
        sx={getGridStyles({ clickableRow: true })}
        onPageChange={setPageNum}
        onRowClick={handleRowClick}
      />
    </PageSection>
  )
}
